import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { IMCMapView } from '../../../../classes/mcMapView';
import { ESRI } from '../../../../esri-loader-util';
import { BooleanConverter, InputConverter, JSONConverter } from '../../../../inputConverter';
import { LayerService } from '../../../../services/layer.service';
import { BaseComponent } from '../../../base.component';

let Point: __esri.PointConstructor;
const degreeName = 'degrees (long/lat)';
const degreeReverseName = 'degrees (lat/long)';

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-coordinates-ng',
    styleUrls: ['../../../sharedStyles/base.scss', './coordinates.component.scss'],
    templateUrl: './coordinates.component.html'
})

export class CoordinatesComponent extends BaseComponent implements AfterViewInit {
    /**
     * Overall state of the component. Values: true = User may use tool, false = tool is inactive.
     */
    @Input()
    set active(value: boolean) {
        this._active = BooleanConverter(value);
        value ? this.activate() : this.deactivate();
        this.activeChange.emit(value);
    }
    get active(): boolean { return this._active; }
    private _active: boolean = false;

    @Output() readonly activeChange: EventEmitter<boolean> = new EventEmitter();

    @Input()
    set currentMode(value: 'live' | 'capture') {
        this._currentMode = value;
        if (this.coordinateConversionTool) {
            this.coordinateConversionTool.mode = this._currentMode;
        }
    }
    get currentMode() {
        return this._currentMode;
    }
    _currentMode: 'live' | 'capture' = 'live';

    @Input()
    @InputConverter(JSONConverter)
    formatSort = [degreeReverseName, degreeName, 'dd', 'ddm', 'dms', 'mgrs', 'usng', 'utm'];

    mapRef: IMCMapView;
    coordinateConversionTool: __esri.CoordinateConversion;
    currentLocationX = null;
    currentLocationY = null;
    autoCreateLayer: boolean = false;
    mapClickListener;
    coordWidgetConstructor: __esri.CoordinateConversionConstructor;
    coordWidgetViewModel: __esri.CoordinateConversionViewModelConstructor;
    pictureMarkerConstructor: __esri.PictureMarkerSymbolConstructor;
    formatConstructor: __esri.FormatConstructor;
    markerSymbol: __esri.PictureMarkerSymbol;

    @ViewChild('coordinates') coordinatesContainer: ElementRef<HTMLDivElement>;

    constructor(_vcr: ViewContainerRef, private readonly changeDetectorRef: ChangeDetectorRef, private readonly layerService: LayerService) {
        super(_vcr);
        this.registerEvent('activeChange', this.activeChange);
    }

    async ngAfterViewInit() {
        this.mapRef = await this.setupMapViewRef(null);
        Point = await ESRI.Point;
    }

    async createTool() {
        [this.coordWidgetConstructor, this.coordWidgetViewModel, this.pictureMarkerConstructor, this.formatConstructor] = await Promise.all([ESRI.CoordinateWidget, ESRI.CoordinateWidgetViewModel, ESRI.PictureMarker, ESRI.Format]);
        this.markerSymbol = new this.pictureMarkerConstructor({
            url: 'https://static.arcgis.com/images/Symbols/Cartographic/esriCartographyMarker_70_White.png',
            height: 24,
            width: 24,
            xoffset: 0,
            yoffset: 12
        });
        const customView = new this.coordWidgetViewModel({
            locationSymbol: this.markerSymbol,
            view: this.mapRef
        });
        const containerNode = document.createElement('div');
        this.coordinatesContainer.nativeElement.append(containerNode);
        this.coordinateConversionTool = new this.coordWidgetConstructor({
            container: containerNode,
            orientation: 'expand-down',
            view: this.mapRef,
            viewModel: customView
        });

        this.coordinateConversionTool.formats.on('before-add', (e) => {
            switch (e.item.name) {
                case 'dms':
                    this.modifyDMSFormat(e.item);
                    break;
                case 'xy':
                    this.modifyXYFormat(e.item);
                    this.duplicateDegrees(e.item);
                    break;
                case 'basemap':
                    e.preventDefault();
                    break;
                default:
                    break;
            }
        });
        this.coordinateConversionTool.formats.on('after-add', (_e) => {
            if (this.coordinateConversionTool.formats.length === this.formatSort.length && !this.sorting) {
                this.sorting = true;
                this.coordinateConversionTool.formats.sort((a, b) => {
                    const aIndex = this.formatSort.indexOf(a.name.toLowerCase());
                    const bIndex = this.formatSort.indexOf(b.name.toLowerCase());
                    if (aIndex < bIndex) {
                        return -1;
                    } else if (aIndex > bIndex) {
                        return 1;
                    }
                    return 0;
                });
                this.sorting = false;
            }
        });
        if (!this.mapClickListener) {
            this.mapClickListener = this.mapRef.on('click', () => {
                if (this.autoCreateLayer && this.coordinateConversionTool) {
                    this.createCoordinateLayer();
                }
            });
        }
        this.coordinateConversionTool.watch(['mode', 'currentLocation'], (newValue, _oldValue, propertyName) => {
            if (propertyName === 'mode') {
                this._currentMode = newValue;
            }
            //
            if (propertyName === 'currentLocation') {
                const current: __esri.Point = newValue;
                if (!current) {
                    this.currentLocationX = null;
                    this.currentLocationY = null;
                } else {
                    this.currentLocationX = current.x;
                    this.currentLocationY = current.y;
                }
            }
            this.changeDetectorRef.detectChanges();
        });
        window.addEventListener('unload', this.unloadListner);

        (<any>this.coordinateConversionTool).when(() => {
            const dropdownButton = this.coordinatesContainer.nativeElement.querySelector('.esri-coordinate-conversion__tools > li.esri-widget--button') as HTMLLIElement;
            dropdownButton.dispatchEvent(new MouseEvent('click', { bubbles: false }));

            if (this.currentMode !== this.coordinateConversionTool.mode) {
                const duration = 1000;
                setTimeout(() => {
                    this.coordinateConversionTool.mode = this.currentMode;
                }, duration);

            }
        });
    }

    private sorting = false;

    private modifyXYFormat(latLongFormat: __esri.Format) {
        latLongFormat.name = degreeName;
        latLongFormat.defaultPattern = latLongFormat.currentPattern = 'X, Y';
        latLongFormat.conversionInfo = {
            reverseConvert: (input: string) => {
                let retVal: __esri.Point = null;
                const [x, y] = input.split(',').map(p => parseFloat(p.trim()));
                if (x && y) {
                    retVal = new Point({ longitude: x, latitude: y });
                }
                return retVal;
            },
            convert: (input: __esri.Point) => {
                return { location: input, coordinate: [input.longitude, input.latitude].join(', ') };
            }
        } as any;
    }

    private duplicateDegrees(latLongFormat: __esri.Format) {
        this.coordinateConversionTool.formats.add(new this.formatConstructor({
            conversionInfo: {
                reverseConvert: (input: string) => {
                    let retVal: __esri.Point = null;
                    const [y, x] = input.split(',').map(p => parseFloat(p.trim()));
                    if (x && y) {
                        retVal = new Point({ longitude: x, latitude: y });
                    }
                    return retVal;
                },
                convert: (input: __esri.Point) => {
                    return { location: input, coordinate: [input.latitude, input.longitude].join(', ') };
                }
            } as any,
            coordinateSegments: [...latLongFormat.coordinateSegments].reverse(),
            currentPattern: 'Y, X',
            defaultPattern: 'Y, X',
            name: degreeReverseName
        }));
    }

    private modifyDMSFormat(dmsFormat: __esri.Format) {
        const parseFix = ((val: string) => parseFloat(val).toFixed());
        (<any>dmsFormat.coordinateSegments.find(cs => cs.alias === 'D')).substitution.output = parseFix;
        (<any>dmsFormat.coordinateSegments.find(cs => cs.alias === 'B')).substitution.output = parseFix;
        (<any>dmsFormat.coordinateSegments.find(cs => cs.alias === 'X')).substitution.output = parseFix;
        (<any>dmsFormat.coordinateSegments.find(cs => cs.alias === 'Y')).substitution.output = parseFix;
        dmsFormat.defaultPattern = dmsFormat.currentPattern = `X°C'D"E, Y°A'B"N`;
        const degreeConversion = ((val: string) => {
            const minutesPerHour = 60;
            const secondsPerHour = 3600;
            const degrees = parseFloat(val.slice(0, val.indexOf('°') - 1));
            const minutes = parseFloat(val.slice(val.indexOf('°') + 1, val.indexOf("'") - 1));
            const seconds = parseFloat(val.slice(val.indexOf("'") + 1, val.indexOf('"') - 1));
            const hemisphere = val.slice(val.indexOf('"') + 1).trim().toLowerCase();
            let decimalDegrees = degrees + (minutes / minutesPerHour) + (seconds / secondsPerHour);
            if (hemisphere === 'w' || hemisphere === 's') {
                decimalDegrees = -Math.abs(decimalDegrees);
            }
            return decimalDegrees;
        });
        dmsFormat.conversionInfo = {
            reverseConvert: (input: string) => {
                let retVal: __esri.Point = null;
                // TODO: Currently broken in ESRI JS API, revisit when updated
                // Before the input is passed here it is parsed by ESRI's code to something unusable
                // Unmodified version is broken as well, though that appears to just have swapped coordinates
                const [x, y] = input.split(',').map(p => p.trim());
                if (x && y
                    && x.includes('°') && x.includes("'") && x.includes('"')
                    && y.includes('°') && y.includes("'") && y.includes('"')) {
                    const convertedX = degreeConversion(x);
                    const convertedY = degreeConversion(y);
                    if (convertedX && convertedY) {
                        retVal = new Point({ longitude: convertedX, latitude: convertedY });
                    }
                }
                return retVal;
            }
        } as any;
    }

    async createCoordinateLayer() {
        const graphic = await ESRI.Graphic;
        const point = {
            type: 'point',
            longitude: this.coordinateConversionTool.currentLocation.longitude,
            latitude: this.coordinateConversionTool.currentLocation.latitude
        };
        const newGraphic = new graphic({
            geometry: point,
            symbol: {
                type: 'simple-marker',
                color: [0, 128, 255],
                size: 14
            }
        } as __esri.GraphicProperties);
        const graphicsLayer = await this.layerService.addGraphicsLayer({
            title: `Location: ${this.coordinateConversionTool.conversions.getItemAt(0).displayCoordinate}`
        });
        graphicsLayer.add(newGraphic);
        this.layerService.mapLayersSub.next();
    }

    /**
     * Make the tool active/shown.
     */
    private async activate() {
        if (!this.coordinateConversionTool) {
            this.createTool();
        }
    }

    /**
     * Make the tool inactive/hidden.
     */
    private deactivate(): void {
        if (this.coordinateConversionTool) {
            this.coordinateConversionTool.destroy();
            this.coordinateConversionTool = null;
        }
        window.removeEventListener('unload', this.unloadListner);
        if (this.mapClickListener) {
            this.mapClickListener.remove();
            this.mapClickListener = null;
        }
    }

    private readonly unloadListner = () => {
        this.deactivate();
    }
}
